// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState, useCallback } from 'react';
import type { MutationOperator, MutationState } from '../../../core/operators/MutationOperator';

/**
 * Return type for useMutation hook
 */
export interface UseMutationResult<TData, TVariables> {
  /** Execute mutation with variables */
  mutate: (variables: TVariables) => void;
  /** Execute mutation with variables and return Promise */
  mutateAsync: (variables: TVariables) => Promise<TData>;
  /** True while mutation is executing */
  isLoading: boolean;
  /** Error object if mutation failed */
  error: Error | null;
  /** Result data from successful mutation */
  data: TData | null;
  /** Reset mutation state to initial values */
  reset: () => void;
}

/**
 * React hook for framework-agnostic MutationOperator
 *
 * Subscribes to a MutationOperator's state and provides mutation functions.
 * Handles null operators gracefully and cleans up subscriptions on unmount.
 *
 * @param mutationOperator - MutationOperator instance or null
 * @returns UseMutationResult with mutate functions and state
 *
 * @example
 * ```tsx
 * function CreateTaskForm() {
 *   const db = useDatabase();
 *   const mutationOperator = useMemo(() => {
 *     if (!db) return null;
 *     const taskOps = new TaskOperator(db);
 *     return taskOps.createMutation();
 *   }, [db]);
 *
 *   const { mutate, isLoading, error, data } = useMutation(mutationOperator);
 *
 *   const handleSubmit = (e) => {
 *     e.preventDefault();
 *     mutate({
 *       title: 'New Task',
 *       description: 'Task description',
 *       orgId: 'org_123',
 *       userId: 'user_456',
 *       projectId: 'proj_789',
 *       milestoneId: 'mile_101'
 *     });
 *   };
 *
 *   if (isLoading) return <div>Creating task...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *   if (data) return <div>Task created: {data.id}</div>;
 *
 *   return <form onSubmit={handleSubmit}>...</form>;
 * }
 * ```
 */
export function useMutation<TData, TVariables>(
  mutationOperator: MutationOperator<TData, TVariables> | null
): UseMutationResult<TData, TVariables> {
  const [state, setState] = useState<MutationState<TData>>({
    data: null,
    isLoading: false,
    error: null,
  });

  useEffect(() => {
    // If no operator provided, reset to initial state
    if (!mutationOperator) {
      setState({
        data: null,
        isLoading: false,
        error: null,
      });
      return;
    }

    // Subscribe to operator's state observable
    const subscription = mutationOperator.state$.subscribe({
      next: setState,
    });

    // Cleanup subscription on unmount or when operator changes
    // Note: We only unsubscribe here. The operator lifecycle is managed
    // by the parent component that created it.
    return () => {
      subscription.unsubscribe();
    };
  }, [mutationOperator]);

  /**
   * Execute mutation without returning Promise (fire-and-forget)
   */
  const mutate = useCallback(
    (variables: TVariables) => {
      if (!mutationOperator) {
        console.warn('useMutation: Cannot mutate without mutation operator');
        return;
      }

      mutationOperator.execute(variables).catch((error) => {
        // Error is already handled by operator and state is updated
        console.error('Mutation error:', error);
      });
    },
    [mutationOperator]
  );

  /**
   * Execute mutation and return Promise for async/await usage
   */
  const mutateAsync = useCallback(
    async (variables: TVariables): Promise<TData> => {
      if (!mutationOperator) {
        throw new Error('useMutation: Cannot mutate without mutation operator');
      }

      return mutationOperator.execute(variables);
    },
    [mutationOperator]
  );

  /**
   * Reset mutation state to initial values
   */
  const reset = useCallback(() => {
    if (mutationOperator) {
      mutationOperator.reset();
    }
  }, [mutationOperator]);

  return {
    mutate,
    mutateAsync,
    isLoading: state.isLoading,
    error: state.error,
    data: state.data,
    reset,
  };
}
